import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useQuotes } from "../hooks/useQuotes";
import { useRoleAccess } from "../../users/hooks/useRoleAccess";
import type { QuoteWithRelations } from "@cleanpro/common-ui";

import { useQuoteActions } from "../hooks/useQuoteActions";
import {
  supabase,
  Quote,
  QuoteStatus,
  QUOTE_STATUS,
  getStatusColor,
  getStatusLabel,
  DateUtils,
  NumberUtils,
  BusinessFeeUtils,
} from "@cleanpro/common-ui";
import BroadcastQuoteModal from "./BroadcastQuoteModal";
import AssignBusinessModal from "./AssignBusinessModal";
import { formatExclusiveTimeRemaining, isExclusivePeriodActive } from "../exclusiveUtils";
import {
  ArrowLeft,
  AlertCircle,
  MapPin,
  Calendar,
  FileText,
  Send,
  XCircle,
  Radio,
  Building2,
  Clock,
  DollarSign,
  CheckCircle,
} from "lucide-react";

/**
 * Displays detailed information for a single quote, including customer info, line items, workflow context,
 * timeline, and available actions. Handles loading, error, and not-found states, and supports status updates
 * (e.g., sending, accepting, rejecting, cancelling, revising) via Supabase. Attempts to load quote data from
 * context for fast reactivity, falling back to Supabase fetch if not found. Also provides navigation to related
 * entities (requests, jobs) and edit functionality if permitted.
 *
 * @component
 * @returns {JSX.Element} The rendered quote detail view.
 *
 * @remarks
 * - Requires a quote ID from the route params.
 * - Uses context data if available for performance.
 * - Handles all quote status transitions and updates timestamps accordingly.
 * - Displays workflow relationships and allows navigation to related resources.
 * - Shows a timeline of quote events (created, sent, viewed, accepted, etc.).
 * - Renders customer and service details, line items, totals, terms, and notes.
 * - Action buttons are shown based on user permissions and quote status.
 *
 * @example
 * ```tsx
 * <Route path="/quotes/:id" element={<Quote />} />
 * ```
 */
export default function QuoteDetail() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const permissions = useRoleAccess();
  const { getToken } = useAuth();

  const [quote, setQuote] = useState<QuoteWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [broadcastSuccess, setBroadcastSuccess] = useState<string | null>(null);
  const [broadcastResponses, setBroadcastResponses] = useState<{
    broadcasts: Array<{
      id: string;
      business: { id: string; business_name: string; email: string | null; phone: string | null };
      status: string;
      broadcast_at: string;
      responded_at: string | null;
    }>;
    rejections: Array<{
      id: string;
      business: { id: string; business_name: string; email: string | null; phone: string | null };
      rejection_reason: string | null;
      rejection_notes: string | null;
      rejected_at: string;
    }>;
  }>({ broadcasts: [], rejections: [] });

  const { quotes, getQuoteById, loading: isQuotesLoading } = useQuotes();

  const { updateStatus, broadcast } = useQuoteActions(
    quote as Quote | null,
    (updates) => {
      if (quote) {
        setQuote({ ...quote, ...updates });
      }
    }
  );

  const handleStatusUpdate = async (status: QuoteStatus) => {
    try {
      setUpdating(true);
      await updateStatus(status);
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setUpdating(false);
    }
  };

  const handleBroadcastQuote = async (businessIds: string[]) => {
  if (!quote) {
    console.error("Cannot broadcast: quote is not loaded");
    return;
  }

  try {
    setUpdating(true);

    console.log("Starting broadcast for quote:", quote.id);
    console.log("Quote data check:", {
      hasService: !!quote.service,
      serviceName: quote.service?.name,
      hasProperty: !!quote.property,
      hasRequest: !!quote.request,
    });

    // Get Clerk session token for Supabase edge function authentication
    const token = await getToken({ template: 'supabase' });
    
    if (!token) {
      throw new Error('Not authenticated. Please log in and try again.');
    }

    // Call the broadcast action to update quote status and create broadcast records
    // Note: We don't set global Authorization headers because Clerk's user ID format
    // (user_*) conflicts with Supabase's UUID expectation in RLS policies
    await broadcast(businessIds);

    // Fetch business details for all selected businesses
    const { data: businesses, error: businessError } = await supabase
      .from("businesses")
      .select("id, business_name, email, phone")
      .in("id", businessIds);

    if (businessError) {
      console.error("Failed to fetch business details:", businessError);
      throw businessError;
    }

    if (!businesses || businesses.length === 0) {
      console.warn("No businesses found for broadcasting");
      setShowBroadcastModal(false);
      return;
    }

    // Calculate contractor payout for display
    const estimatedPayout = BusinessFeeUtils.calculateContractorPayout(
      quote.totalAmount
    );

    console.log("Broadcasting to", businesses.length, "businesses");

    // Send broadcast emails to all businesses in parallel
    const emailPromises = businesses.map(async (business) => {
      try {
        console.log(
          `Generating tokens for business: ${business.business_name}`
        );

        // Generate secure JWT tokens for accept/reject links via Edge Function
        const { data: tokenData, error: tokenError } = await supabase.functions.invoke(
          'generate-quote-token',
          {
            body: {
              quoteId: quote.id,
              businessId: business.id,
            },
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (tokenError) {
          console.error("Token generation failed:", tokenError);
          throw new Error(
            tokenError.message || "Failed to generate quote token"
          );
        }

        const { acceptLink, rejectLink } = tokenData;

        console.log(
          `Tokens generated for ${business.business_name}, preparing email data...`
        );

        // Prepare email data with safe defaults
        const serviceName = quote.service?.name || "Cleaning Service";
        const requestedDateTime =
          quote.request?.requestedDateTime || new Date().toISOString();
        const address = `${quote.property?.addressLine1 || 'Address to be confirmed'}${quote.property?.addressLine2 ? ', ' + quote.property.addressLine2 : ''}, ${quote.property?.city || ''}, ${quote.property?.state || ''} ${quote.property?.postalCode || ''}`;

        console.log("Email data prepared:", {
          serviceName,
          requestedDateTime,
          address,
        });

        // Call secure edge function to send notifications
        // Edge function uses NotificationService server-side with secure API key
        const { data: notificationResult, error: notificationError } = await supabase.functions.invoke(
          'send-provider-quote-broadcast',
          {
            body: {
              recipient: {
                name: business.business_name,
                email: business.email,
                phone: business.phone,
                smsOptIn: false, // Business SMS doesn't require opt-in for transactional
              },
              quoteData: {
                quoteNumber: quote.quoteNumber,
                serviceName,
                requestedDate: DateUtils.formatDate(requestedDateTime, "long"),
                requestedTime: DateUtils.formatTimeFromDate(requestedDateTime),
                address,
                propertyType: quote.property?.propertyType,
                propertySquareFootage: quote.property?.squareFootage,
                propertyBedrooms: quote.property?.bedrooms,
                propertyBathrooms: quote.property?.bathrooms,
                estimatedPayout: NumberUtils.formatCurrency(estimatedPayout),
                acceptQuoteLink: acceptLink,
                rejectQuoteLink: rejectLink,
              },
            },
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (notificationError) {
          throw notificationError;
        }

        // Update broadcast record to mark as sent
        await supabase
          .from("quote_broadcasts")
          .update({
            updated_at: new Date().toISOString(),
          })
          .eq("quote_id", quote.id)
          .eq("business_id", business.id);

        if (notificationResult.email.success) {
          console.log(`✓ Email sent to ${business.business_name}`);
        } else {
          console.error(`✗ Email failed to ${business.business_name}:`, notificationResult.email.error);
        }
        
        if (notificationResult.sms?.success) {
          console.log(`✓ SMS sent to ${business.business_name} at ${business.phone}`);
        } else if (notificationResult.sms?.error) {
          console.error(`✗ SMS failed to ${business.business_name}:`, notificationResult.sms.error);
        }
        
        console.log(`✓ Broadcast notifications sent to ${business.business_name}`);
      } catch (emailError) {
        console.error(
          `✗ Failed to send broadcast email to ${business.business_name}:`,
          emailError
        );

        // Log email error in broadcast record (keep status as 'pending' since provider hasn't responded)
        // Note: Email delivery failure is different from provider declining the quote
        await supabase
          .from("quote_broadcasts")
          .update({
            response_notes: `Email delivery failed: ${emailError instanceof Error ? emailError.message : 'Unknown error'}`,
            updated_at: new Date().toISOString(),
          })
          .eq("quote_id", quote.id)
          .eq("business_id", business.id);

        // Don't throw - continue sending to other businesses
      }
    });

    // Wait for all emails (but don't fail if some emails fail)
    const results = await Promise.allSettled(emailPromises);

    const successCount = results.filter(
      (r) => r.status === "fulfilled"
    ).length;
    const failureCount = results.filter(
      (r) => r.status === "rejected"
    ).length;

    console.log(
      `Broadcast complete: ${successCount} sent, ${failureCount} failed`
      );

      setShowBroadcastModal(false);

      // Show appropriate success message
      if (failureCount === 0) {
        setBroadcastSuccess(`Quote successfully broadcast to ${successCount} business(es)!`);
      } else {
        setBroadcastSuccess(
          `Quote broadcast complete. ${successCount} sent successfully, ${failureCount} failed. Check logs for details.`
        );
      }
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setBroadcastSuccess(null);
      }, 5000);
  } catch (err) {
    console.error("Failed to broadcast quote:", err);
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    alert(`Failed to broadcast quote: ${errorMessage}`);
  } finally {
    setUpdating(false);
  }
};

  /**
   * Assigns a business to a quote, updates the quote status, creates a job, and sends notification emails.
   *
   * This function performs the following steps:
   * 1. Updates the quote with the selected business ID and sets its status to 'assigned'.
   * 2. Fetches necessary quote data for job creation.
   * 3. Creates a new job associated with the quote and business.
   *    - If job creation fails, rolls back the quote update.
   * 4. Fetches business details for email notifications.
   * 5. Sends job scheduled email to the customer and job assignment email to the business.
   *    - Email sending errors are logged but do not fail the main operation.
   * 6. Updates local state and shows a success message.
   *
   * @param businessId - The ID of the business to assign to the quote.
   * @returns {Promise<void>} Resolves when the assignment and notifications are complete.
   *
   * @throws Will show an alert and log errors if any step fails.
   */
  const handleAssignBusiness = async (businessId: string) => {
    if (!quote) return;

    try {
      setUpdating(true);

      // Update the quote with the business_id and status = 'assigned'
      // Note: The database trigger 'on_quote_update_create_job' will automatically
      // create a job when business_id is set and status is 'assigned'
      const { error: updateError } = await supabase
        .from("quotes")
        .update({
          business_id: businessId,
          status: QUOTE_STATUS.ASSIGNED,
          updated_at: new Date().toISOString(),
        })
        .eq("id", quote.id);

      if (updateError) {
        console.error("Failed to update quote:", updateError);
        throw updateError;
      }

      // Fetch the newly created job (created by database trigger)
      // Add retry logic with delay to handle async trigger execution
      let jobData = null;
      let jobError = null;
      const retries = 3;

      for (let i = 0; i < retries; i++) {
        // Exponential backoff delay to allow trigger to complete (e.g., 100ms, 200ms, 400ms)
        if (i > 0) {
          const baseDelayMs = 100;
          const delayMs = baseDelayMs * Math.pow(2, i - 1);
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        const result = await supabase
          .from("jobs")
          .select(
            `
            *,
            service:services(),
            business:businesses(),
            customer:customers(),
            property:properties()
          `
          )
          .eq("quote_id", quote.id)
          .maybeSingle(); // Use maybeSingle() instead of single() to avoid 406 error

        jobData = result.data;
        jobError = result.error;

        if (jobData) {
          console.log("Job found on attempt", i + 1);
          break;
        }
      }

      if (jobError || !jobData) {
        console.error("Failed to fetch created job after retries:", jobError);
        console.log("Trigger may not have fired, creating job manually via RPC...");
        
        // Create job using RPC function that bypasses RLS
        const { error: rpcError } = await supabase
          .rpc('create_job_from_quote', {
            p_quote_id: quote.id,
            p_business_id: businessId
          });

        if (rpcError) {
          console.error("Failed to create job via RPC:", rpcError);
          // Rollback quote update
          await supabase
            .from("quotes")
            .update({ 
              business_id: null, 
              status: QUOTE_STATUS.PENDING_ASSIGNMENT 
            })
            .eq("id", quote.id);
          throw new Error("Failed to create job: " + rpcError.message);
        }

        console.log("Job created via RPC, now fetching job details...");
        
        // Fetch the newly created job with all relations
        const { data: createdJob, error: fetchError } = await supabase
          .from("jobs")
          .select(
            `
            *,
            service:services(),
            business:businesses(),
            customer:customers(),
            property:properties()
          `
          )
          .eq("quote_id", quote.id)
          .single();

        if (fetchError || !createdJob) {
          console.error("Failed to fetch RPC-created job:", fetchError);
          throw new Error("Job created but could not fetch details: " + (fetchError?.message || "Unknown error"));
        }

        jobData = createdJob;
        console.log("Job fetched successfully:", jobData);
      }

      // Fetch business details for email notification
      const { data: businessData } = await supabase
        .from("businesses")
        .select("business_name, email, phone")
        .eq("id", businessId)
        .single();

      // Step 5: Send emails to customer and business
      const emailPromises = [];

      // Send job scheduled email to customer
      if (quote.customer?.email && quote.customer?.name) {
        const customerEmail = quote.customer.email;
        const customerName = quote.customer.name;

        emailPromises.push(
          (async () => {
            try {
              // Use quote context data since we already have it loaded
              const serviceName = quote.service?.name || "Cleaning Service";
              const businessName = businessData?.business_name || "Our Team";
              const businessPhone =
                businessData?.phone || "Contact for details";

              // Build full address from quote property data
              let fullAddress = "Address to be confirmed";
              if (quote.property?.addressLine1) {
                const parts = [quote.property.addressLine1];
                if (quote.property.addressLine2) {
                  parts.push(quote.property.addressLine2);
                }
                parts.push(
                  `${quote.property.city}, ${quote.property.state} ${quote.property.postalCode}`
                );
                fullAddress = parts.join(", ");
              }

              console.log(
                "Preparing to send job scheduled email with data:",
                jobData
              );

              // Call secure edge function to send notifications
              // Edge function uses NotificationService server-side with secure API key
              const { error: notificationError } = await supabase.functions.invoke(
                'send-customer-job-scheduled',
                {
                  body: {
                    recipient: {
                      name: customerName,
                      email: customerEmail,
                      phone: quote.customer?.phone,
                      smsOptIn: quote.customer?.smsOptIn,
                    },
                    jobData: {
                      serviceName,
                      businessName,
                      businessPhone,
                      scheduledDate: DateUtils.formatDate(
                        jobData.date_time || new Date().toISOString(),
                        "long"
                      ),
                      scheduledTime: DateUtils.formatTimeFromDate(
                        jobData.date_time || new Date().toISOString()
                      ),
                      address: fullAddress,
                      price: NumberUtils.formatCurrency(jobData.price),
                      jobId: jobData.id,
                    },
                  },
                }
              );

              if (notificationError) {
                throw notificationError;
              }
            } catch (emailError) {
              console.error(
                "Failed to send job scheduled notifications to customer:",
                emailError
              );
            }
          })()
        );
      }

      // Send job assignment email to business
      if (businessData?.email && businessData?.business_name) {
        emailPromises.push(
          (async () => {
            try {
              const contractorPayout =
                BusinessFeeUtils.calculateContractorPayout(quote.totalAmount);

              // Call Supabase Edge Function instead of using EmailService directly
              // This keeps the Brevo API key secure on the server
              const { error: emailError } = await supabase.functions.invoke(
                'send-provider-job-assigned',
                {
                  body: {
                    recipient: {
                      name: businessData.business_name,
                      email: businessData.email,
                      phone: businessData.phone,
                    },
                    quoteData: {
                      quoteNumber: quote.quoteNumber,
                      serviceName: quote.service?.name || "Cleaning Service",
                      requestedDate: DateUtils.formatDate(
                        quote.request?.requestedDateTime ||
                          new Date().toISOString(),
                        "long"
                      ),
                      requestedTime: DateUtils.formatTimeFromDate(
                        quote.request?.requestedDateTime || new Date().toISOString()
                      ),
                      propertyAddressLine1:
                        quote.property?.addressLine1 || "Address to be confirmed",
                      propertyType: quote.property?.propertyType,
                      propertySquareFootage: quote.property?.squareFootage,
                      propertyBedrooms: quote.property?.bedrooms,
                      propertyBathrooms: quote.property?.bathrooms,
                      totalAmount: NumberUtils.formatCurrency(quote.totalAmount),
                      businessPayout: NumberUtils.formatCurrency(contractorPayout),
                      price: NumberUtils.formatCurrency(quote.totalAmount),
                    },
                  },
                }
              );

              if (emailError) throw emailError;
            } catch (emailError) {
              console.error(
                "Failed to send job assignment email to business:",
                emailError
              );
            }
          })()
        );
      }

      // Wait for all emails to be sent (but don't fail if emails fail)
      await Promise.allSettled(emailPromises);

      // NOTE: We do NOT call broadcast() here because this is a direct assignment by admin
      // The broadcast() function sets status to 'pending_assignment', which would override 'assigned'
      // Broadcasting is only for when quotes are sent to multiple businesses to compete for

      // Update local state
      setQuote({
        ...quote,
        status: QUOTE_STATUS.ASSIGNED,
        updatedAt: new Date().toISOString(),
      });

      setShowAssignModal(false);

      // Show success message
      alert("Business assigned successfully! A job has been created.");
    } catch (err) {
      console.error("Failed to assign business:", err);
      alert("Failed to assign business. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId) {
        setError("Quote ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const cached = quotes.find((item) => item.id === quoteId) ?? null;
        if (cached) {
          setQuote(cached);
        }

        const resolved = await getQuoteById(quoteId);
        if (resolved) {
          setQuote(resolved);
        } else {
          setError("Quote not found");
        }
      } catch (err) {
        console.error("Failed to fetch quote:", err);
        setError("Failed to fetch quote");
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId, quotes, getQuoteById]);

  // Fetch broadcast responses when quote is loaded
  useEffect(() => {
    const fetchBroadcastResponses = async () => {
      if (!quote?.id) return;

      try {
        // Fetch broadcasts with business details
        const { data: broadcasts, error: broadcastError } = await supabase
          .from('quote_broadcasts')
          .select(`
            id,
            status,
            broadcast_at,
            responded_at,
            business:business_id (
              id,
              business_name,
              email,
              phone
            )
          `)
          .eq('quote_id', quote.id)
          .order('broadcast_at', { ascending: false });

        if (broadcastError) {
          console.error('Failed to fetch broadcasts:', broadcastError);
        }

        console.log('Raw broadcasts data:', broadcasts);

        // Fetch rejections with business details
        const { data: rejections, error: rejectError } = await supabase
          .from('quote_rejections')
          .select(`
            id,
            rejection_reason,
            rejection_notes,
            rejected_at,
            business:business_id (
              id,
              business_name,
              email,
              phone
            )
          `)
          .eq('quote_id', quote.id)
          .order('rejected_at', { ascending: false });

        if (rejectError) {
          console.error('Failed to fetch rejections:', rejectError);
        }

        console.log('Raw rejections data:', rejections);

        const processedBroadcasts = (broadcasts || []).map(b => {
          console.log('Processing broadcast:', b);
          return {
            ...b,
            broadcast_at: b.broadcast_at || new Date().toISOString(),
          };
        });

        console.log('Processed broadcasts:', processedBroadcasts);

        setBroadcastResponses({
          broadcasts: processedBroadcasts,
          rejections: rejections || [],
        });
      } catch (err) {
        console.error('Error fetching broadcast responses:', err);
      }
    };

    fetchBroadcastResponses();
  }, [quote?.id]);

  if (loading || isQuotesLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-gray-500">Loading quote details...</div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/quotes")}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quotes
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Quote Not Found
          </h3>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  // Calculate contractor payout details
  const platformFee = BusinessFeeUtils.calculatePlatformFee(quote.totalAmount);
  const contractorPayout = BusinessFeeUtils.calculateContractorPayout(
    quote.totalAmount
  );
  const feePercentage = BusinessFeeUtils.getPlatformFeePercentage();

  {
    console.log("All the quote data:", quote);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Message Banner */}
      {broadcastSuccess && (
        <div className="bg-green-50 border-b border-green-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-green-800">{broadcastSuccess}</p>
              </div>
              <button
                onClick={() => setBroadcastSuccess(null)}
                className="flex-shrink-0 text-green-600 hover:text-green-800 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/quotes")}
                className="flex items-center text-gray-500 hover:text-gray-700 transition-colors duration-200 group"
              >
                <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                Back to Quotes
              </button>
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  Quote Details
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Quote{" "}
                  <span className="font-medium text-gray-900">
                    {quote.quoteNumber}
                  </span>{" "}
                  for{" "}
                  <span className="font-medium text-gray-900">
                    {quote.customer?.name}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span
                className={`inline-flex px-4 py-2 text-sm font-semibold rounded-full border-2 ${getStatusColor(
                  quote.status,
                  "quote"
                )}`}
              >
                {getStatusLabel(quote.status, "quote")}
              </span>
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg shadow-lg">
                <div className="text-xs uppercase tracking-wide font-medium opacity-90">
                  Total Amount
                </div>
                <div className="text-2xl font-bold">
                  {NumberUtils.formatCurrency(quote.totalAmount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Exclusive Quote Banner */}
        {quote.isExclusive && quote.business && quote.request?.exclusiveUntil && (
          <div className={`rounded-lg border-2 p-6 ${
            isExclusivePeriodActive(quote.request.exclusiveUntil)
              ? 'bg-indigo-50 border-indigo-300'
              : 'bg-gray-50 border-gray-300'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className={`h-6 w-6 ${
                    isExclusivePeriodActive(quote.request.exclusiveUntil)
                      ? 'text-indigo-600'
                      : 'text-gray-500'
                  }`} />
                  <h3 className={`text-lg font-bold ${
                    isExclusivePeriodActive(quote.request.exclusiveUntil)
                      ? 'text-indigo-900'
                      : 'text-gray-700'
                  }`}>
                    Exclusive Quote
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Provider:</span>
                    <span className="text-sm text-gray-900 font-semibold">
                      {quote.business.businessName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Expires:</span>
                    <span className="text-sm text-gray-900">
                      {DateUtils.formatDate(quote.request.exclusiveUntil, 'long')} at{' '}
                      {DateUtils.formatTimeFromDate(quote.request.exclusiveUntil)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Time Remaining:</span>
                    <span className={`text-sm font-bold ${
                      isExclusivePeriodActive(quote.request.exclusiveUntil)
                        ? 'text-indigo-600'
                        : 'text-red-600'
                    }`}>
                      {formatExclusiveTimeRemaining(quote.request.exclusiveUntil)}
                    </span>
                  </div>
                </div>
              </div>
              {quote.status === QUOTE_STATUS.EXCLUSIVE_PENDING && isExclusivePeriodActive(quote.request.exclusiveUntil) && (
                <div className="flex-shrink-0 ml-4">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white border-2 border-indigo-700 shadow-lg">
                    <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse"></span>
                    Active
                  </span>
                </div>
              )}
              {(!isExclusivePeriodActive(quote.request.exclusiveUntil) || quote.exclusiveExpiredAt) && (
                <div className="flex-shrink-0 ml-4">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-gray-600 text-white border-2 border-gray-700">
                    Expired
                  </span>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                This quote was submitted via {quote.business.businessName}'s direct link and is exclusively offered to them for 24 hours. 
                {isExclusivePeriodActive(quote.request.exclusiveUntil) 
                  ? ' After the exclusive period expires, it can be broadcast to other providers.'
                  : ' The exclusive period has ended.'}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Schedule & Location */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Requested Schedule
              </h3>
              <div className="space-y-3">
                {/* Date */}
                {quote.request?.requestedDateTime && (
                  <div className="flex items-start space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {DateUtils.formatDateLong(quote.request.requestedDateTime)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Time */}
                {quote.request?.requestedDateTime && (
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {DateUtils.formatTimeFromDate(quote.request.requestedDateTime, true)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Address */}
                {quote.property?.addressLine1 && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">
                        <button
                          onClick={() =>
                            quote.property?.id &&
                            navigate(`/properties/${quote.property.id}`)
                          }
                          className="text-indigo-600 hover:text-indigo-700 font-medium"
                          style={{
                            color: "transparent",
                            textShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
                          }}
                        >
                          {quote.property.addressLine1}
                        </button>
                      </p>
                      {quote.property.addressLine2 && (
                        <p className="text-gray-600">
                          {quote.property.addressLine2}
                        </p>
                      )}
                      <p className="text-gray-600">
                        {quote.property.city}, {quote.property.state}{" "}
                        {quote.property.postalCode}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Property Details - Compact */}
              {quote.property &&
                (quote.property.bedrooms ||
                  quote.property.bathrooms ||
                  quote.property.squareFootage) && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Property Details
                    </h4>
                    <div className="flex flex-wrap gap-4 text-sm">
                      {quote.property.propertyType && (
                        <div className="flex items-center">
                          <span className="text-gray-500">Type:</span>
                          <span className="ml-1 text-gray-900 capitalize">
                            {quote.property.propertyType}
                          </span>
                        </div>
                      )}
                      {(quote.property.bedrooms ||
                        quote.property.bathrooms) && (
                        <div className="flex items-center">
                          <span className="text-gray-500">Rooms:</span>
                          <span className="ml-1 text-gray-900">
                            {quote.property.bedrooms || 0} bed /{" "}
                            {quote.property.bathrooms || 0} bath
                          </span>
                        </div>
                      )}
                      {quote.property.squareFootage && (
                        <div className="flex items-center">
                          <span className="text-gray-500">Size:</span>
                          <span className="ml-1 text-gray-900">
                            {quote.property?.squareFootage} sq ft
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Service & Duration */}
              {(quote.service?.name || quote.request?.estimatedDuration || quote.lawnAreaSqft) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Service Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    {quote.service?.name && (
                      <div>
                        <span className="text-gray-500">Service:</span>
                        {quote.service.id ? (
                          <button
                            onClick={() =>
                              quote.service?.id &&
                              navigate(`/services/${quote.service.id}`)
                            }
                            className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium"
                          >
                            {quote.service.name}
                          </button>
                        ) : (
                          <span className="ml-1 text-gray-900 font-medium">
                            {quote.service.name}
                          </span>
                        )}
                      </div>
                    )}
                    {quote.request?.estimatedDuration && (
                      <div>
                        <span className="text-gray-500">
                          Approximate Duration:
                        </span>
                        <span className="ml-1 text-gray-900">
                          {DateUtils.formatDurationByMinutes(
                            quote.request.estimatedDuration
                          )}
                        </span>
                      </div>
                    )}
                    {quote.lawnAreaSqft && (
                      <div>
                        <span className="text-gray-500">
                          Lawn Area:
                        </span>
                        <span className="ml-1 text-gray-900">
                          {quote.lawnAreaSqft.toLocaleString()} sq ft
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes & Instructions */}
              {(quote.request?.notes ||
                quote.request?.accessInstructions ||
                quote.property?.specialInstructions) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Notes & Instructions
                  </h4>
                  <div className="space-y-3">
                    {quote.request?.notes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Customer Notes
                        </p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          {quote.request.notes}
                        </p>
                      </div>
                    )}
                    {quote.request?.accessInstructions && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Access Instructions
                        </p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          {quote.request.accessInstructions}
                        </p>
                      </div>
                    )}
                    {quote.property?.specialInstructions && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Special Instructions
                        </p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          {quote.property.specialInstructions}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Quote Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Quote</h3>
                <span className="text-sm text-gray-500 font-mono">
                  #{quote.quoteNumber}
                </span>
              </div>

              {/* Line Items */}
              <div className="space-y-2 mb-4">
                {quote.lineItems.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="flex justify-between text-gray-900"
                  >
                    <span className="text-sm">
                      {item.description}
                      {item.quantity > 1 && ` (x${item.quantity})`}
                    </span>
                    <span className="text-sm font-medium">
                      {NumberUtils.formatCurrency(item.total || 0)}
                    </span>
                  </div>
                ))}

                {/* Discount */}
                {quote.discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600 pt-1">
                    <span>Discount</span>
                    <span>
                      -{NumberUtils.formatCurrency(quote.discountAmount)}
                    </span>
                  </div>
                )}

                {/* Tax */}
                <div className="flex justify-between text-sm">
                  <span>Tax ({quote.taxRate.toFixed(2)}%)</span>
                  <span>{NumberUtils.formatCurrency(quote.taxAmount)}</span>
                </div>

                {/* Total */}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>{NumberUtils.formatCurrency(quote.totalAmount)}</span>
                </div>
              </div>

              {/* Quote Terms & Notes */}
              {(quote.terms || quote.customerNotes || quote.validUntil) && (
                <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                  {quote.customerNotes && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Customer Notes
                      </p>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {quote.customerNotes}
                      </p>
                    </div>
                  )}
                  {quote.terms && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Terms & Conditions
                      </p>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {quote.terms}
                      </p>
                    </div>
                  )}
                  {/* {quote.validUntil && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Valid Until</p>
                    <p className="text-sm text-gray-900">{DateUtils.formatDateLong(quote.validUntil)}</p>
                  </div>
                )} */}
                </div>
              )}
            </div>

            {/* Contractor Payout Summary - NEW SECTION */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border-2 border-indigo-200 p-6">
              <div className="flex items-center justify-center mb-6">
                <DollarSign className="h-6 w-6 text-indigo-600 mr-2" />
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
                  Contractor Payout Summary
                </h3>
              </div>

              <div className="space-y-4 bg-white rounded-lg p-5 shadow-inner">
                {/* Total Quote Value */}
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    Total Quote Value (Client Paid)
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {NumberUtils.formatCurrency(quote.totalAmount)}
                  </span>
                </div>

                {/* Platform Fee Deduction */}
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    Go Sit Back Fee ({feePercentage})
                  </span>
                  <span className="text-lg font-bold text-red-600">
                    -{NumberUtils.formatCurrency(platformFee)}
                  </span>
                </div>

                {/* Contractor Net Payout */}
                <div className="flex justify-between items-center pt-2">
                  <span className="text-base font-bold text-gray-900">
                    Contractor Net Payout (To Be Paid)
                  </span>
                  <span className="text-2xl font-bold text-green-600 bg-green-50 px-4 py-2 rounded-lg border-2 border-green-200">
                    {NumberUtils.formatCurrency(contractorPayout)}
                  </span>
                </div>
              </div>

              {/* Read-only Notice */}
              <div className="mt-4 flex items-start space-x-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-3">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p>
                  <span className="font-semibold">Note:</span> These values are
                  calculated automatically based on the total quote amount. The
                  contractor payout represents the final amount to be paid after
                  the {feePercentage} platform fee is deducted.
                </p>
              </div>
            </div>

            {/* Broadcast Responses Section */}
            {(broadcastResponses.broadcasts.length > 0 || broadcastResponses.rejections.length > 0) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Broadcast Responses
                  </h3>
                  <span className="text-sm text-gray-500">
                    {broadcastResponses.broadcasts.length} broadcast(s)
                  </span>
                </div>

                {/* Accepted Response */}
                {broadcastResponses.broadcasts.filter(b => b.status === 'accepted').map((broadcast) => (
                  <div key={broadcast.id} className="mb-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-green-900">
                            Accepted by {broadcast.business.business_name}
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            {broadcast.business.email} • {broadcast.business.phone}
                          </p>
                          {broadcast.responded_at && (
                            <p className="text-xs text-green-600 mt-2">
                              Accepted on {DateUtils.formatDate(broadcast.responded_at, 'long')} at{' '}
                              {DateUtils.formatTimeFromDate(broadcast.responded_at)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pending Broadcasts */}
                {broadcastResponses.broadcasts.filter(b => b.status === 'pending').length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Pending Responses</h4>
                    <div className="space-y-2">
                      {broadcastResponses.broadcasts.filter(b => b.status === 'pending').map((broadcast) => (
                        <div key={broadcast.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  {broadcast.business.business_name}
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                  {broadcast.business.email}
                                </p>
                                <p className="text-xs text-yellow-700 mt-1">
                                  Sent {DateUtils.formatDate(broadcast.broadcast_at, 'long')}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejections */}
                {broadcastResponses.rejections.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Declined ({broadcastResponses.rejections.length})
                    </h4>
                    <div className="space-y-2">
                      {broadcastResponses.rejections.map((rejection) => (
                        <div key={rejection.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <XCircle className="h-5 w-5 text-gray-500 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {rejection.business.business_name}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                {rejection.business.email}
                              </p>
                              {rejection.rejection_reason && (
                                <p className="text-xs text-gray-700 mt-2">
                                  <span className="font-medium">Reason:</span>{' '}
                                  {rejection.rejection_reason.split('_').map(word => 
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                  ).join(' ')}
                                </p>
                              )}
                              {rejection.rejection_notes && (
                                <p className="text-xs text-gray-600 mt-1 italic">
                                  "{rejection.rejection_notes}"
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-2">
                                Declined {DateUtils.formatDate(rejection.rejected_at, 'long')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Internal Notes
                </h3>
              </div>
              <div className="p-6 space-y-6">
                {/* Internal Notes  */}
                {quote.internalNotes && (
                  <div>
                    <p className="text-gray-900 text-sm whitespace-pre-wrap mt-1">
                      {quote.internalNotes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            {/* Enhanced Quote Actions */}
            {permissions.canEditQuotes && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full mr-3"></div>
                    Actions
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  {quote.status === QUOTE_STATUS.DRAFT && (
                    <>
                      <button
                        onClick={() => handleStatusUpdate(QUOTE_STATUS.DRAFT)}
                        disabled={updating}
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        <FileText className="h-5 w-5" />
                        <span className="font-medium">
                          {updating ? "Updating..." : "Edit Quote"}
                        </span>
                      </button>

                      <button
                        onClick={() =>
                          handleStatusUpdate(QUOTE_STATUS.AWAITING_RESPONSE)
                        }
                        disabled={updating}
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        <Send className="h-5 w-5" />
                        <span className="font-medium">
                          {updating ? "Updating..." : "Send to Customer"}
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          handleStatusUpdate(QUOTE_STATUS.ARCHIVED)
                        }
                        disabled={updating}
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        <XCircle className="h-5 w-5" />
                        <span className="font-medium">
                          {updating ? "Updating..." : "Mark as Archived"}
                        </span>
                      </button>
                    </>
                  )}

                  {quote.status === QUOTE_STATUS.AWAITING_RESPONSE && (
                    <>
                      <button
                        onClick={() =>
                          handleStatusUpdate(QUOTE_STATUS.APPROVED)
                        }
                        disabled={updating}
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        <Send className="h-5 w-5" />
                        <span className="font-medium">
                          {updating ? "Sending..." : "Mark as Approved"}
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          handleStatusUpdate(QUOTE_STATUS.ARCHIVED)
                        }
                        disabled={updating}
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        <XCircle className="h-5 w-5" />
                        <span className="font-medium">
                          {updating ? "Updating..." : "Mark as Archived"}
                        </span>
                      </button>
                    </>
                  )}

                  {quote.status === QUOTE_STATUS.APPROVED && (
                    <>
                      <button
                        onClick={() => setShowBroadcastModal(true)}
                        disabled={updating}
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        <Radio className="h-5 w-5" />
                        <span className="font-medium">
                          {updating ? "Sending..." : "Broadcast to Service Providers"}
                        </span>
                      </button>

                      <button
                        onClick={() => setShowAssignModal(true)}
                        disabled={updating}
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        <Building2 className="h-5 w-5" />
                        <span className="font-medium">
                          {updating ? "Assigning..." : "Assign to Service Provider"}
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Quote Timeline */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                  Timeline
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-gray-700">
                      Created
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      {quote.createdAt
                        ? `${DateUtils.formatDateLong(quote.createdAt)} at ${DateUtils.formatTimeFromDate(quote.createdAt)}`
                        : "N/A"}
                    </p>
                  </div>
                </div>
                {quote.sentAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <label className="text-sm font-semibold text-gray-700">
                        Sent
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {DateUtils.formatDateLong(quote.sentAt)} at{" "}
                        {DateUtils.formatTimeFromDate(quote.sentAt)}
                      </p>
                    </div>
                  </div>
                )}
                {quote.viewedAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <label className="text-sm font-semibold text-gray-700">
                        Viewed
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {DateUtils.formatDateLong(quote.viewedAt)} at{" "}
                        {DateUtils.formatTimeFromDate(quote.viewedAt)}
                      </p>
                    </div>
                  </div>
                )}
                {quote.acceptedAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <label className="text-sm font-semibold text-green-700">
                        Accepted
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {DateUtils.formatDateLong(quote.acceptedAt)} at{" "}
                        {DateUtils.formatTimeFromDate(quote.acceptedAt)}
                      </p>
                    </div>
                  </div>
                )}
                {quote.rejectedAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <label className="text-sm font-semibold text-red-700">
                        Rejected
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {DateUtils.formatDateLong(quote.rejectedAt)} at{" "}
                        {DateUtils.formatTimeFromDate(quote.rejectedAt)}
                      </p>
                    </div>
                  </div>
                )}
                {quote.cancelledAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <label className="text-sm font-semibold text-gray-700">
                        Cancelled
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {DateUtils.formatDateLong(quote.cancelledAt)} at{" "}
                        {DateUtils.formatTimeFromDate(quote.cancelledAt)}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-indigo-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-gray-700">
                      Last Updated
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      {quote.updatedAt
                        ? `${DateUtils.formatDateLong(quote.updatedAt)} at ${DateUtils.formatTimeFromDate(quote.updatedAt)}`
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Relationships */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                  Relationships
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-gray-700">
                      Quote
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      <Link to={`/quotes/${quote.id}`}>
                        Quote #{quote.quoteNumber} (This Page)
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-gray-700">
                      Request
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      <Link to={`/requests/${quote.requestId}`}>
                        Request #{quote.requestId}
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-gray-700">
                      Customer
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      <Link to={`/customers/${quote.customerId}`}>
                        {quote.customerId}
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-gray-700">
                      Property
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      <Link to={`/properties/${quote.property?.id}`}>
                        {quote.property?.id}
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Broadcast Quote Modal */}
        <BroadcastQuoteModal
          isOpen={showBroadcastModal}
          onClose={() => setShowBroadcastModal(false)}
          onBroadcast={handleBroadcastQuote}
          postalCode={quote.property?.postalCode || ""}
          serviceName={quote.service?.name || ""}
        />

        {/* Assign Business Modal */}
        <AssignBusinessModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onAssign={handleAssignBusiness}
          postalCode={quote.property?.postalCode}
        />
      </div>
    </div>
  );
}
